import { inject, Injectable } from '@angular/core';
import { Engagement } from '../data-models/engagement';
import { map } from 'rxjs/operators';
import { EngagementRespondent } from '../data-models/engagement-respondent';
import { EngagementMetric } from '../data-models/engagement-metric';
import { Respondent } from '../data-models/respondent';
import { take } from 'rxjs/operators';
import { RespondentReference } from '../data-models/respondent-reference';
import { EngagementEmailTemplate } from '../data-models/engagement-email-template';
import { EngagementReference } from '../data-models/engagement-reference';
import { LeaverAnalysis } from '../data-models/leaver-analysis';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EngagementAnalysis } from '../data-models/engagement-analysis';
import { EngagementAnalysisTemplate } from '../data-models/engagement-analysis-template';
import { EngagementAnalysisQuestions } from '../data-models/engagement-analysis-questions';
import { EngagementAnswer } from '../data-models/engagement-answer';
import { EngagementComment } from '../data-models/engagement-comment';
import { GlobalsService } from './globals.service';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { Database, equalTo, get, off, onValue, orderByChild, push, query, ref, remove, set, update } from '@angular/fire/database';
import { Auth } from '@angular/fire/auth';
import { PushidService } from './pushid.service';
import { ChartResponseData } from '../data-models/chart-response-data';

@Injectable({
  providedIn: 'root'
})
export class EngagementService {

  sEndpoint: string = 'https://apis.zenithr.net/api/';//'https://apis.zenithr.net/api/';//
  sDevEndpoint: string = 'http://localhost:52619/api/';
  fbFunctionURL: string = '';

  pushIdService = inject(PushidService);

  constructor(private db: Database, public afAuth: Auth, private http: HttpClient, private globals: GlobalsService) {
    this.sEndpoint = this.globals.sEndpoint;
    this.fbFunctionURL = this.globals.fbFunctionURL;

  }

  updateEngagement(tenantKey: string, companyKey: string, engagement: Engagement) {
    const engagementRef = ref(
      this.db,
      `/TenantData/${tenantKey}/Engagements/${companyKey}/${engagement.key}`
    );
    return update(engagementRef, engagement);
  }


  fetchEngagements(tenantKey: string, companyKey: string): Observable<any[]> {
    const engagementsRef = ref(this.db, `/TenantData/${tenantKey}/Engagements/${companyKey}`);
    const q = query(engagementsRef, orderByChild('isDeleted'), equalTo(false));

    return new Observable<any[]>(subscriber => {
      const listener = onValue(q, snapshot => {
        const results: any[] = [];
        snapshot.forEach(childSnap => {
          results.push({ key: childSnap.key, ...childSnap.val() });
        });
        subscriber.next(results);
      }, error => {
        subscriber.error(error);
      });

      // Unsubscribe when the observable is completed
      return () => off(q, 'value', listener);
    });
  }


  fetchEngagementsDraft(tenantKey: string, companyKey: string) {
    const engagementsRef = ref(this.db, `/TenantData/${tenantKey}/Engagements/${companyKey}`);
    const q = query(engagementsRef, orderByChild('status'), equalTo('DRAFT'));

    return from(get(q)).pipe(
      map(snapshot => {
        const results: Engagement[] = [];
        snapshot.forEach(childSnap => {
          results.push({ key: childSnap.key!, ...childSnap.val() });
        });
        return results;
      }),
      take(1)
    );
  }

  fetchEngagementsOnce(tenantKey: string, companyKey: string) {
    const engagementsRef = ref(this.db, `/TenantData/${tenantKey}/Engagements/${companyKey}`);
    const q = query(engagementsRef, orderByChild('isDeleted'), equalTo(false));

    return from(get(q)).pipe(
      map(snapshot => {
        const results: Engagement[] = [];
        snapshot.forEach(childSnap => {
          results.push({ key: childSnap.key!, ...childSnap.val() });
        });
        return results;
      }),
      take(1)
    );
  }

  fetchEngagement(tenantKey: string, companyKey: string, engagementKey: string) {
    const engagementRef = ref(this.db, `/TenantData/${tenantKey}/Engagements/${companyKey}/${engagementKey}`);

    return from(get(engagementRef)).pipe(
      map(snapshot => ({
        key: snapshot.key!,
        ...snapshot.val()
      }) as Engagement),
      take(1)
    );
  }

  // fetchEngagementRespondentList(tenantKey: string, companyKey: string, engagementKey: string, filterByMetrics?: EngagementMetric[]) {
  //   let request = {
  //     SurveyType: 'ENG',
  //     TenantKey: tenantKey,
  //     CompanyKey: companyKey,
  //     SurveyKey: engagementKey,
  //     FilterByMetrics: filterByMetrics
  //   }
  //   return this.http.post(this.sEndpoint + 'Engagement/FetchRespondentList', request);
  // }

  createEngagementResList(request) {
    return this.http.post(this.sEndpoint + 'Engagement/CreateEngagementResList', request);
  }

  updateRespondentDetails(request) {
    return this.http.post(this.sEndpoint + 'Engagement/UpdateRespondent', request);
  }

  fetchEngagementAnswersSDB(request) {
    return this.http.post(this.sEndpoint + 'Engagement/ClosedEngagement', request);
  }

  getAppliedEntity(request) {
    return this.http.post(this.sEndpoint + 'Engagement/GetAppliedEntity', request);
  }

  GetEngagementCommentsForSection(request) {
    return this.http.post(this.sEndpoint + 'Engagement/GetEngagementCommentsForSection', request);
  }

  GetAllEngagementCommentsForSection(request) {
    return this.http.post(this.sEndpoint + 'Engagement/GetAllEngagementCommentsForSection', request);
  }

  GetEngagementCommentsForQuestion(request) {
    return this.http.post(this.sEndpoint + 'Engagement/GetEngagementCommentsForQuestion', request);
  }

  populateResponseCriteriaChart(request) {
    return this.http.post(this.sEndpoint + 'Engagement/PopulateResponseCriteriaChart', request);
  }

  populateResponseCriteria(request) {
    return this.http.post(this.sEndpoint + 'Engagement/PopulateResponseCriteria', request);
  }

  populateResponseCharts(request) {
    return this.http.post(this.sEndpoint + 'Engagement/PopulateResponseCharts', request);
  }

  fetchPreviousScores(request) {
    return this.http.post(this.sEndpoint + 'Engagement/GetPreviousData', request);
  }

  getTreeList(request) {
    return this.http.post(this.sEndpoint + 'Engagement/GetEntityTreeList', request);
  }

  fetchIndexAndQuestionAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Engagement/IndexAndQuestionAnalysis', request);
  }

  insertSurveyAnswers(request) {
    return this.http.post(this.sEndpoint + 'Engagement/SubmitAnswers', request);
  }

  updateFinalSurvey(request) {
    return this.http.post(this.sEndpoint + 'UpdateSurveyDetails', request);
  }

  sendEngagementReminders(request) {
    return this.http.post(this.sEndpoint + 'Engagement/SendEngagementReminders', request);
  }
  updateCommentSentiment(request) {
    return this.http.post(this.sEndpoint + 'Engagement/UpdateCommentSentiment', request);
  }
  fetchQuestionAnalysis(request) {
    return this.http.post(this.sEndpoint + 'Engagement/FetchQuestionAISentiment', request);
  }
  fetchEngagementTopics(request) {
    return this.http.post(this.sEndpoint + 'Engagement/FetchTopics', request);
  }
  fetchRespondentsFromSDB(request) {
    return this.http.post(this.fbFunctionURL + 'fetchRespondentsFromDB', request);
  }

  updateEngagementRespondentInDB(request) {
    return this.http.put(this.fbFunctionURL + 'updateEngagementRespondentInDB', request);
  }

  fetchRespondents(tenantKey: string, companyKey: string) {
    const respondentsRef = ref(this.db, `/TenantData/${tenantKey}/RespondentList/${companyKey}`);

    return from(get(respondentsRef)).pipe(
      map(snapshot => {
        const data: Respondent[] = [];
        snapshot.forEach(child => {
          data.push({ key: child.key!, ...child.val() });
        });
        return data;
      })
    );
  }

  deleteEngagementRespondent(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    respondentKey: string
  ) {
    const respondentRef = ref(
      this.db,
      `/TenantData/${tenantKey}/EngagementDetails/${companyKey}/${engagementKey}/EngagementRespondentList/${respondentKey}`
    );

    return remove(respondentRef);
  }

  fetchEngagementMetrics(
    tenantKey: string,
    companyKey: string,
    engagementKey: string
  ) {
    const metricsRef = ref(
      this.db,
      `/TenantData/${tenantKey}/EngagementDetails/${companyKey}/${engagementKey}/EngagementRespondentMetrics/`
    );

    return from(get(metricsRef)).pipe(
      map(snapshot => {
        const data = snapshot.val() || {};
        return Object.entries(data).map(([key, value]: [string, any]) => ({
          key,
          ...value
        }));
      })
    );
  }


  fetchSurveySnapshot(request) {
    return this.http.post(this.sEndpoint + 'Engagement/FetchSurveySnapshot', request);
  }



  fetchEngagementComparisonData(request) {
    return this.http.post(this.fbFunctionURL + 'fetchTenantEngagements', request);

  }

  updateClosedEngagementData(
    tenantKey: string,
    companyKey: string,
    companyName: any[],
    engagement: Engagement,
    engagementAnalysis: EngagementAnalysis
  ) {
    const tenantEngagementData = {
      key: engagement.key,
      title: engagement.title,
      description: engagement.description,
      unixStart: engagement.unixStart,
      companyKey,
      workplaceEngagementScore: engagementAnalysis.workPlaceEngagementScore,
      companyName,
      engagementDrivers: engagementAnalysis.engagementDrivers.map(driver => {
        const driverName = driver.name.find(n => n.key === 'EN');
        return {
          key: driverName?.name || null,
          name: driver.name,
          score: driver.meanScore
        };
      }),
      lowlyEngaged: engagementAnalysis.lowlyEngaged,
      moderatlyEngaged: engagementAnalysis.moderatlyEngaged,
      highlyEngaged: engagementAnalysis.highlyEngaged
    };

    const engagementRef = ref(
      this.db,
      `/TenantData/${tenantKey}/EngagementDataAnalysis/${tenantEngagementData.key}`
    );

    return update(engagementRef, tenantEngagementData);
  }

  fetchPredefinedMetrics(
    tenantKey: string,
    companyKey: string,
    respondentKey: string
  ): Observable<any[]> {
    const metricsRef = ref(
      this.db,
      `/TenantData/${tenantKey}/RespondentDetails/${companyKey}/${respondentKey}`
    );

    return from(get(metricsRef)).pipe(
      map(snapshot => {
        const data = snapshot.val();
        if (!data) return [];
        return Object.entries(data).map(([key, value]: [string, any]) => ({
          key,
          ...value
        }));
      })
    );
  }

  fetchRespondentFromPool(
    tenantKey: string,
    companyKey: string,
    respondentEmail: string
  ): Observable<Respondent[]> {
    const respondentsRef = ref(
      this.db,
      `/TenantData/${tenantKey}/RespondentList/${companyKey}`
    );

    const q = query(respondentsRef, orderByChild('email'), equalTo(respondentEmail));

    return from(get(q)).pipe(
      map(snapshot => {
        const data = snapshot.val();
        if (!data) return [];
        return Object.entries(data).map(([key, value]: [string, any]) => ({
          key,
          ...value
        }));
      })
    );
  }

  fetchPoolRespondent(request) {
    return this.http.post(this.sEndpoint + 'Respondent/FetchPoolRespondent', request);

  }


  updatePredefinedMetrics(
    tenantKey: string,
    companyKey: string,
    respondentKey: string,
    metrics: any[]
  ) {
    const metricsRef = ref(
      this.db,
      `/TenantData/${tenantKey}/RespondentDetails/${companyKey}/${respondentKey}`
    );
    return set(metricsRef, metrics);
  }

  updateEngagementRespondent(tenantKey: string, companyKey: string, engagementKey: string, engagementRespondents: EngagementRespondent[], userKey?, sendEmail: boolean = false, companyName?: any[], engagement?: Engagement) {
    for (let i = 0; i < engagementRespondents.length; i++) {
      let engagementRespondent: EngagementRespondent = engagementRespondents[i];
      if (!engagementRespondent.key) {
        engagementRespondent.key = this.pushIdService.generatePushId();
        engagementRespondent.referenceKey = this.pushIdService.generatePushId();
        engagementRespondent.companyKey = companyKey;
        engagementRespondent.tenantKey = tenantKey;
      }
      engagementRespondent.createdBy = 'SELF';
      engagementRespondent.referenceKey = engagementRespondent.referenceKey;
    }
    let request = {
      SurveyType: 'ENG',
      TenantKey: tenantKey,
      CompanyKey: companyKey,
      SurveyKey: engagementKey,
      respondents: engagementRespondents,
      sendEmail: sendEmail,
      CompanyName: companyName,
      surveyInfo: engagement
    }
    return this.http.post(this.sEndpoint + 'Respondent/UpdateSurveyRespondent', request);
  }

  updateWellbeingRespondent(tenantKey: string, companyKey: string, engagementKey: string, engagementRespondents: EngagementRespondent[], userKey?, sendEmail: boolean = false, companyName?: any[], engagement?: Engagement) {
    for (let i = 0; i < engagementRespondents.length; i++) {
      let engagementRespondent: EngagementRespondent = engagementRespondents[i];
      if (!engagementRespondent.key) {
        engagementRespondent.key = this.pushIdService.generatePushId();
        engagementRespondent.referenceKey = this.pushIdService.generatePushId();
        engagementRespondent.companyKey = companyKey;
        engagementRespondent.tenantKey = tenantKey;
      }
      engagementRespondent.createdBy = 'SELF';
      engagementRespondent.referenceKey = engagementRespondent.referenceKey;
    }
    let request = {
      SurveyType: 'WLB',
      TenantKey: tenantKey,
      CompanyKey: companyKey,
      SurveyKey: engagementKey,
      respondents: engagementRespondents,
      sendEmail: sendEmail,
      CompanyName: companyName,
      surveyInfo: engagement
    }
    return this.http.post(this.sEndpoint + 'Respondent/UpdateSurveyRespondent', request);
  }

  updateCultureRespondent(tenantKey: string, companyKey: string, engagementKey: string, engagementRespondents: EngagementRespondent[], userKey?, sendEmail: boolean = false, companyName?: any[], engagement?: Engagement) {
    for (let i = 0; i < engagementRespondents.length; i++) {
      let engagementRespondent: EngagementRespondent = engagementRespondents[i];
      if (!engagementRespondent.key) {
        engagementRespondent.key = this.pushIdService.generatePushId();
        engagementRespondent.referenceKey = this.pushIdService.generatePushId();
        engagementRespondent.companyKey = companyKey;
        engagementRespondent.tenantKey = tenantKey;
      }
      engagementRespondent.createdBy = 'SELF';
      engagementRespondent.referenceKey = engagementRespondent.referenceKey;
    }
    let request = {
      SurveyType: 'CLT',
      TenantKey: tenantKey,
      CompanyKey: companyKey,
      SurveyKey: engagementKey,
      respondents: engagementRespondents,
      sendEmail: sendEmail,
      CompanyName: companyName,
      surveyInfo: engagement
    }
    return this.http.post(this.sEndpoint + 'Respondent/UpdateSurveyRespondent', request);
  }

  updateEngagementReference(respondentReference: RespondentReference) {
    const refPath = ref(this.db, `/EngagementReferences/${respondentReference.key}`);
    return update(refPath, respondentReference);
  }


  updateEngagementMetrics(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    metrics: EngagementMetric[]
  ) {
    const metricsRef = ref(
      this.db,
      `/TenantData/${tenantKey}/EngagementDetails/${companyKey}/${engagementKey}/EngagementRespondentMetrics/`
    );

    // Generate push IDs for metrics without a key
    const metricsWithKeys: Record<string, EngagementMetric> = {};
    metrics.forEach(metric => {
      const metricKey = metric.key || push(metricsRef).key!;
      metricsWithKeys[metricKey] = { ...metric, key: metricKey };
    });

    return set(metricsRef, metricsWithKeys);
  }

  updateRespondentList(
    tenantKey: string,
    companyKey: string,
    respondent: Respondent
  ) {
    const respondentRef = ref(
      this.db,
      `/TenantData/${tenantKey}/RespondentList/${companyKey}/${respondent.key}`
    );
    return update(respondentRef, respondent);
  }


  removeRespondentFromPool(
    tenantKey: string,
    companyKey: string,
    respondentKey: string
  ) {
    const respondentRef = ref(
      this.db,
      `/TenantData/${tenantKey}/RespondentList/${companyKey}/${respondentKey}`
    );
    return remove(respondentRef);
  }

  fetchEngagementQuestionnaire() {
    const q = query(
      ref(this.db, '/Questionnaires/'),
      orderByChild('isEngagement'),
      equalTo(true)
    );

    return from(get(q)).pipe(
      map(snapshot => {
        const result: any[] = [];
        snapshot.forEach(childSnapshot => {
          result.push({ key: childSnapshot.key, ...childSnapshot.val() });
        });
        return result;
      })
    );
  }

  fetchEngagementQuestionnaireObject() {
    return this.http.post(this.fbFunctionURL + 'fetchQuestionnaire', { questionnaireType: 'ENGAGEMENT' });
  }

  bulkAddRespondents(request) {
    return this.http.post(this.fbFunctionURL + 'bulkAddRespondents', request);
  }
  bulkUpdateEngagementSimulationAnswers(request) {
    return this.http.post(this.fbFunctionURL + 'bulkUpdateEngagementSimulationAnswers', request);
  }

  openClosedEngagement(request) {
    return this.http.post(this.fbFunctionURL + 'openClosedEngagement', request);
  }

  updateEngagementRespondentSurvey(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    analysis: LeaverAnalysis
  ) {
    const engagementRef = ref(
      this.db,
      `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/${analysis.key}`
    );
    return update(engagementRef, analysis);
  }

  fetchEngagementTemplates(tenantKey: string, companyKey: string) {
    const templatesRef = ref(this.db, `/TenantData/${tenantKey}/EngagementTemplates/${companyKey}`);
    return from(get(templatesRef)).pipe(
      map(snapshot => {
        const data: EngagementEmailTemplate[] = [];
        snapshot.forEach(childSnap => {
          data.push({ key: childSnap.key!, ...childSnap.val() } as EngagementEmailTemplate);
        });
        return data;
      }),
      take(1)
    );
  }



  fetchEngagementReference(engagementReference: string) {
    const referenceRef = ref(this.db, `/EngagementReferences/${engagementReference}`);
    return from(get(referenceRef)).pipe(
      map(snapshot => {
        if (!snapshot.exists()) return null;
        return { key: snapshot.key!, ...snapshot.val() } as EngagementReference;
      }),
      take(1)
    );
  }

  fetchEngagementReferences(companyKey: string) {
    const referencesQuery = query(
      ref(this.db, '/EngagementReferences/'),
      orderByChild('companyKey'),
      equalTo(companyKey)
    );

    return from(get(referencesQuery)).pipe(
      map(snapshot => {
        const result: EngagementReference[] = [];
        snapshot.forEach(childSnap => {
          result.push({ key: childSnap.key!, ...childSnap.val() } as EngagementReference);
          return false; // continue iteration
        });
        return result;
      })
    );
  }


  removeEngagement(tenantKey: string, companyKey: string, engagementKey: string) {
    const engagementRef = ref(
      this.db,
      `/TenantData/${tenantKey}/Engagements/${companyKey}/${engagementKey}/isDeleted`
    );
    return set(engagementRef, true);
  }

  removeEngagementRespondentList(tenantKey: string, companyKey: string, engagementKey: string) {
    return remove(ref(this.db, `/TenantData/${tenantKey}/EngagementDetails/${companyKey}/${engagementKey}`));
  }

  removeEngagementRespondentAnalysis(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    respondentKey: string
  ) {
    const analysisRef = ref(
      this.db,
      `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/${respondentKey}`
    );
    return remove(analysisRef);
  }

  removeEngagementAnalysis(
    tenantKey: string,
    engagementKey: string,
    companyKey?: string,
    surveyType: 'ENG' | 'HAP' = 'ENG'
  ) {
    const analysisRef = ref(this.db, `/TenantData/${tenantKey}/EngagementDataAnalysis/${engagementKey}`);
    const rtdbRemove = remove(analysisRef);

    // Dual-delete SQL snapshot (Phase 1); ignore API failures so RTDB delete still wins
    if (companyKey) {
      this.http.post(this.sEndpoint + 'Comparison/Remove', {
        TenantKey: tenantKey,
        CompanyKey: companyKey,
        SurveyKey: engagementKey,
        SurveyType: surveyType
      }).subscribe({ error: () => { } });
    }

    return rtdbRemove;
  }

  removeEngagementReference(refKey: string) {
    const refRef = ref(this.db, `/EngagementReferences/${refKey}`);
    return remove(refRef);
  }

  fetchEngagementReferencesByEngKey(engagementKey: string) {
    const referencesQuery = query(
      ref(this.db, '/EngagementReferences/'),
      orderByChild('engagementKey'),
      equalTo(engagementKey)
    );
    return from(get(referencesQuery)).pipe(
      map(snapshot => {
        const data: any[] = [];
        snapshot.forEach(child => {
          data.push({ key: child.key, ...child.val() });
        });
        return data;
      }),
      take(1)
    );
  }



  fetchEngagementAnalysis(tenantKey: string, companyKey: string, engagementKey: string) {
    const analysisRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}`);
    return from(get(analysisRef)).pipe(
      map(snapshot => {
        const data: any[] = [];
        snapshot.forEach(child => {
          data.push({ key: child.key, ...child.val() });
        });
        return data;
      }),
      take(1)
    );
  }

  // registerEngagementAnalysisTemplate() {
  //   return this.db.object<Respondent>('/TenantData/' + tenantKey + '/RespondentList/' + companyKey + '/' + respondent.key).update(respondent)
  // }

  checkBalanceAndCreditEngagement(request) {
    return this.http.post(this.fbFunctionURL + 'creditEngagement', request);
  }


  fetchBenchmark(request) {
    return this.http.post(this.fbFunctionURL + 'fetchAllEngagementBenchmark', request);
  }

  insertEngagementAnalysisTemplate(engagementAnalysisTemplate: EngagementAnalysisTemplate) {
    const templateRef = ref(
      this.db,
      `/EngagementAnalysis/${engagementAnalysisTemplate.tenantKey}/${engagementAnalysisTemplate.companyKey}/${engagementAnalysisTemplate.engagementKey}`
    );
    return from(update(templateRef, engagementAnalysisTemplate));
  }

  insertEngagementTemplateQuestions(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    engagementAnalysisQuestion: EngagementAnalysisQuestions
  ) {
    const questionRef = ref(
      this.db,
      `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Questions/${engagementAnalysisQuestion.key}`
    );
    return from(update(questionRef, engagementAnalysisQuestion));
  }

  forceSetEngagementTemplate(engagementAnalysisTemplate: EngagementAnalysisTemplate) {
    let request = { engagementTemplate: engagementAnalysisTemplate };
    return this.http.post('http://localhost:5001/exitanalysis-a6325/us-central1/forceSetEngagementTemplate', request);
  }


  migrateAnswers(rAnalysis: any, engagementKey) {
    let request = { rAnalysis: rAnalysis, engagementKey: engagementKey };
    return this.http.post('http://localhost:5001/exitanalysis-a6325/us-central1/migrateAnswers', request);
  }

  engagementGoLive(request) {
    return this.http.post(this.fbFunctionURL + 'engagementGoLive', request);
  }


  insertEngagementTemplate(request) {
    return this.http.post(this.sEndpoint + 'SurveyDetails/SurveyGoLive', request);
  }

  forceDeleteAnswers(tenantKey: string, companyKey: string, engagementKey: string) {
    const answersRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Answers`);
    return from(remove(answersRef));
  }

  forceDeleteComments(tenantKey: string, companyKey: string, engagementKey: string) {
    const commentsRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Comments`);
    return from(remove(commentsRef));
  }

  insertEngagementAnswers(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    answerKey: string,
    engagementAnswer: EngagementAnswer
  ) {
    const answerRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Answers/${answerKey}`);
    return from(update(answerRef, engagementAnswer));
  }

  insertEngagementComment(
    tenantKey: string,
    companyKey: string,
    engagementKey: string,
    answerKey: string,
    engagementComment: EngagementComment
  ) {
    const commentRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Comments/${answerKey}`);
    return from(update(commentRef, engagementComment));
  }

  insertMessUp() {
    const messRef = ref(this.db, `/EngagementAnalysis/-MbVraWsn_BxD8DeAgaM`);
    return from(update(messRef, { key: '' }));
  }

  fetchEngagementAnswers(tenantKey: string, companyKey: string, engagementKey: string) {
    const answersRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Answers`);
    return from(get(answersRef)).pipe(
      map(snapshot => {
        const data: EngagementAnswer[] = [];
        snapshot.forEach(child => {
          data.push({ key: child.key, ...child.val() });
        });
        return data;
      }),
      take(1)
    );
  }

  fetchEngagementAnswersOnce(request) {
    return this.http.post(this.fbFunctionURL + 'fetchEngagementAnswers', request);
  }

  removeLocalEngagementAnswers(request) {
    return this.http.post(this.fbFunctionURL + 'removeLocalEngagementAnswers', request);
  }


  fetchEngagementComments(tenantKey: string, companyKey: string, engagementKey: string) {
    const commentsRef = ref(this.db, `/EngagementAnalysis/${tenantKey}/${companyKey}/${engagementKey}/Comments`);
    return from(get(commentsRef)).pipe(
      map(snapshot => {
        const data: EngagementComment[] = [];
        snapshot.forEach(child => {
          data.push({ key: child.key, ...child.val() });
        });
        return data;
      }),
      take(1)
    );
  }

  fetchEngagementCommentsFromSDB(request) {
    return this.http.post(this.fbFunctionURL + 'fetchCommentsFromSDB', request);
  }
  private analyticsSurvey = new BehaviorSubject<AnalyticsEngagement>(null);
  analyticsSurvey$ = this.analyticsSurvey.asObservable();

  setAnalyticsSurvey(filters: AnalyticsEngagement) {
    this.analyticsSurvey.next(filters);
  }
  selectedCriterias$: BehaviorSubject<EngagementMetric[] | any> = new BehaviorSubject(['']);
  surveyData$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);



  /**
   * Bulk delete respondents in live view for enagement
   * 
   * If IsDeleteAll is True, RespondentIds in the Ids property would be skipped
   * If IsDeleteAll is False, RespondentIds in the Ids property would be used to delete respondents
   * 
   * Ids hold Respondent ID as an array
   * @param request 
   * @returns 
   */
  bulkDeleteRespondents(request: BulkDeleteRequest) {
    return this.http.post(`${this.sEndpoint}Engagement/BulkDeleteRespondents`, request);
  }

  /**
   * Clone Survey 
   * @param request 
   * @returns 
   */
  cloneSurvey(request: CloneSurveyRequest) {
    return this.http.post(`${this.sEndpoint}Engagement/CloneSurvey`, request);
  }

  GetEngagementLiveAnalysis(tenantKey: string, companyKey: string, surveyKey: string, Criteria: string): Observable<any> {
    let params = new HttpParams()
      .set('TenantKey', tenantKey)
      .set('CompanyKey', companyKey)
      .set('SurveyKey', surveyKey)
      .set('Criteria', Criteria);

    return this.http.get<any>(this.sEndpoint + 'Engagement/GetLiveAnalysis', { params });
  }

  GetEnpsAnalysis(criteria, request: EnpsAnalysisRequest): Observable<EnpsAnalysisResponse> {
    return this.http.post<EnpsAnalysisResponse>(`${this.sEndpoint}Engagement/GetEnpsAnalysis/${criteria}`, request);
  }

  getSecureRespondentLink(
    tenantKey: string,
    companyKey: string,
    surveyKey: string,
    email: string,
    employeeId: string
  ): Observable<any> {
    const params = new HttpParams()
      .set('TenantKey', tenantKey)
      .set('CompanyKey', companyKey)
      .set('SurveyKey', surveyKey)
      .set('Email', email)
      .set('EmployeeId', employeeId);

    return this.http.get<any>(this.sEndpoint + 'Engagement/GetSecureRespondentLink', { params });
  }

  GetWorkplaceEngagementByCriteria(criteria, driverKey, request: WorkplaceEngagementByCriteriaRequest): Observable<ChartResponseData[]> {
    return this.http.post<ChartResponseData[]>(`${this.sEndpoint}Engagement/GetWorkplaceEngagementByCriteria/${criteria}/${driverKey}`, request);
  }

}

export interface EnpsAnalysisResponse {
  ChartData: EnpsAnalysisModel[]
  PromotersCount: number,
  DetractorsCount: number,
  PassivesCount: number,
  PromotersPercentage: number,
  PassivesPercentage: number,
  DetractorsPercentage: number,
  OverallPercentage: number,
}

export interface EnpsAnalysisModel {
  Category: string,
  Group: string,
  Value: any,
  Promoters: number,
  Detractors: number,
  Passives: number
}

export interface EnpsAnalysisRequest {
  TenantKey: string,
  CompanyKey: string,
  SurveyKey: string,
  Filters: EngagementMetric[]
}
export interface CloneSurveyRequest {
  TenantKey: string,
  CompanyKey: string,
  SurveyKey: string,
  OriginalSurveyKey: string
}

export interface BulkDeleteRequest {
  TenantKey: string,
  CompanyKey: string,
  SurveyKey: string,
  IsDeleteAll: boolean,
  Ids: any[],
  SearchText?: string
}

export class AnalyticsEngagement {
  tenantKey: string;
  companyKey: string;
  questions: any;
  survey: {
    name: string,
    key: string,
  };
  engagementAnalysis: any
}

export interface WorkplaceEngagementByCriteriaRequest {
  TenantKey: string,
  CompanyKey: string,
  SurveyKey: string,
  Filters: EngagementMetric[]
}